import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useServices } from 'services';
import { MembershipTier } from 'types/SignUpData';
import TierGateDialog from 'components/core/TierGateDialog';
import { meetsTier } from 'utils/tierRank';

interface TierGateContextValue {
  hasTier: (requiredTier: MembershipTier) => boolean;
  requireTier: (requiredTier: MembershipTier, featureLabel: string) => boolean;
}

const TierGateContext = createContext<TierGateContextValue | null>(null);

interface DialogState {
  open: boolean;
  requiredTier: MembershipTier;
  featureLabel: string;
}

export const TierGateProvider: React.FC<{ children: React.ReactNode }> = observer(
  ({ children }) => {
    const { authService } = useServices();
    const currentUser = authService.user;
    const currentTier = currentUser?.membershipTier as MembershipTier | undefined;

    const [dialogState, setDialogState] = useState<DialogState>({
      open: false,
      requiredTier: 'Tier 1',
      featureLabel: '',
    });

    const hasTier = useCallback(
      (requiredTier: MembershipTier): boolean => meetsTier(currentTier, requiredTier),
      [currentTier]
    );

    const requireTier = useCallback(
      (requiredTier: MembershipTier, featureLabel: string): boolean => {
        if (meetsTier(currentTier, requiredTier)) return true;
        setDialogState({ open: true, requiredTier, featureLabel });
        return false;
      },
      [currentTier]
    );

    const handleClose = useCallback((): void => {
      setDialogState((s) => ({ ...s, open: false }));
    }, []);

    const value = useMemo<TierGateContextValue>(
      () => ({ hasTier, requireTier }),
      [hasTier, requireTier]
    );

    return (
      <TierGateContext.Provider value={value}>
        {children}
        <TierGateDialog
          open={dialogState.open}
          onClose={handleClose}
          currentTier={currentTier}
          requiredTier={dialogState.requiredTier}
          featureLabel={dialogState.featureLabel}
        />
      </TierGateContext.Provider>
    );
  }
);

export const useTierGate = (): TierGateContextValue => {
  const ctx = useContext(TierGateContext);
  if (!ctx) {
    throw new Error('useTierGate must be used within a TierGateProvider');
  }
  return ctx;
};